import { AdPlatform, MarketingLeadStatus } from '../../../services/marketingLeads';

export const platformMeta: Record<AdPlatform, { label: string; color: string }> = {
  META: { label: 'Meta Ads', color: '#0081FB' },
  GOOGLE: { label: 'Google Ads', color: '#4285F4' },
  ORGANIC: { label: 'Organic', color: '#22c55e' },
  OTHER: { label: 'Other', color: '#94a3b8' },
};

export const leadStatusMeta: Record<MarketingLeadStatus, { label: string; color: string; bg: string }> = {
  NEW: { label: 'New', color: '#60a5fa', bg: '#1d4ed833' },
  CONTACTED: { label: 'Contacted', color: '#22c55e', bg: '#22c55e22' },
  QUALIFIED: { label: 'Qualified', color: '#a78bfa', bg: '#7c3aed22' },
  CONVERTED: { label: 'Converted', color: '#0BE600', bg: '#0BE60022' },
  LOST: { label: 'Lost', color: '#ef4444', bg: '#ef444422' },
};

export const campaignStatusMeta: Record<string, { label: string; color: string; bg: string }> = {
  ACTIVE: { label: 'Active', color: '#22c55e', bg: '#22c55e22' },
  PAUSED: { label: 'Paused', color: '#f59e0b', bg: '#f59e0b22' },
  ENDED: { label: 'Ended', color: '#94a3b8', bg: '#64748b22' },
  NO_CAMPAIGNS: { label: 'No campaigns', color: '#64748b', bg: '#64748b22' },
};

/**
 * Money, in the currency the connected ad account is billed in.
 *
 * The dashboard used to hard-code "£" onto whatever number came back, which
 * quietly mislabels the figures for an account billed in anything else. The
 * server now returns the account's ISO code on the summary; pass it through.
 */
export const formatCurrency = (value: number | null | undefined, currency = 'GBP'): string => {
  const n = Number(value);
  if (value === null || value === undefined || Number.isNaN(n)) return '—';
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      // Whole pounds for real amounts, but a cost-per-lead of 4.20 must not
      // round to 4.
      maximumFractionDigits: Math.abs(n) > 0 && Math.abs(n) < 1000 ? 2 : 0,
    }).format(n);
  } catch {
    // Unknown/absent ISO code — show the number with the code beside it
    // rather than inventing a symbol.
    return `${currency} ${Math.round(n).toLocaleString('en-GB')}`;
  }
};

/**
 * Money that may simply not have been synced yet.
 *
 * `hasData` false means the insights sync stored no campaign-days for this
 * range at all, so there is no measured amount — "—" is the truth, and "£0"
 * is a claim the data doesn't support.
 */
export const formatCurrencyOrDash = (
  value: number | null | undefined,
  currency: string,
  hasData: boolean,
): string => (hasData ? formatCurrency(value, currency) : '—');

/** @deprecated pass the account currency — see formatCurrency. */
export const formatCurrencyGBP = (value: number) => formatCurrency(value, 'GBP');
